import * as React from "react";
import { isEmpty, isUndefined } from "lodash";

import {
	clearNonNumberCharacters,
	cn,
	Component,
	createCounterMessage,
	I18NString,
	trimString
} from "../../helpers/Utils";
import { IControlError, IControlSchema } from "../../../core/Controls";
import { IDispatcher } from "../../helpers/Dispatcher";
import { phoneFix, TextBoxValidate } from "../../../core/validation/validate";

import Alert from "./Alert";
import { Dictionary } from "../../../types/Dictionary";

export interface I18nSchema extends IControlSchema {
	description: I18NString;
	notification: I18NString;
	maxLength: number;
	minLength: number;
	disabled?: boolean;
	placeholder?: string;
	defaultValue?: {
		ru: string;
		en: string;
	};
	isInteger?: boolean;
	lines?: number;
	value?: string;
	min?: number;
	max?: number;
	label?: string;
	extra: any;
}

export interface I18nProps {
	schema: I18nSchema;
	api: any;
	dispatcher?: IDispatcher;
	errors: Dictionary<IControlError | undefined>,
	visited: boolean;
	parentType: string;
	helpButtonClick: (args: any) => void;
	openNotificationClick: (args: any) => void;
	values?: string
}

interface IState {
	value: string;
	openInfo: boolean;
	openNotification: boolean;
}

const Help = ({ onClick }) => (
	<span onClick={onClick} className="input-group-addon btn btn-default">
		<span className="glyphicon glyphicon-question-sign" />
	</span>
);

const Notification = ({ onClick }) => (
	<span onClick={onClick} className="input-group-addon btn btn-default">
		<span className="glyphicon glyphicon-question-sign" />
	</span>
);

class I18n extends Component<I18nProps, IState> {
	private input: HTMLInputElement | HTMLTextAreaElement;

	state = {
		value: null,
		openInfo: false,
		openNotification: true,
	};

	public componentDidMount() {
		const {
			schema: { value },
			errors: existingError,
			values
		} = this.props;

		if (values) {
			this.setState({ value: values });
			return;
		}

		if (!isEmpty(value)) {
			this.props.dispatcher.onVisit(this.props.schema.id);
		}
		this.setState({ value });

		if (existingError) {
			return;
		}

		const error = TextBoxValidate(value, this.props.schema);
		this.props.dispatcher.setError(
			this.props.schema.id,
			this.input.value,
			error && error.isError ? { message: error.message } : undefined,
			false
		);
	}

	// TODO: Не желательно в апдейте мутировать стейт
	// eslint-disable-next-line react/no-deprecated
	componentWillUpdate?(nextProps: Readonly<I18nProps>, nextState: Readonly<IState>): void {
		if (this.props.schema.value !== nextProps.schema.value && nextState.value !== nextProps.schema.value) {
			// eslint-disable-next-line react/no-will-update-set-state
			this.setState({ value: nextProps.schema.value });
		}
	}

	protected onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		let { value } = event.target;
		if (this.props.schema?.extra?.valueType?.toUpperCase() === "PHONE") {
			value = phoneFix(value);
		}
		event.target.value = value;

		this.setState({
			value: trimString(this.props.schema.maxLength, value),
		});

		// this.throttledChange(value);
	};

	protected onBlur = () => {
		// "SumGrant"
		if (!this.input) {
			return;
		}
		const value = typeof this.input.value === "string" && this.input.value.trim();

		const error = TextBoxValidate(value, this.props.schema);
		this.props.dispatcher.onBlur(
			this.props.schema.id,
			value,
			!error,
			error && error.isError && { message: error.message }
		);
		this.props.dispatcher.onVisit(this.props.schema.id);
	};

	private onNumberInput = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = clearNonNumberCharacters(event.target.value);
		this.setState({ value });
	};

	private onNumberChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const value = trimString(this.props.schema.maxLength, clearNonNumberCharacters(event.target.value));
		this.setState({ value });
		this.throttledChange(value);
	};

	onClick = () => {
		this.setState(({ openInfo }) => ({ openInfo: !openInfo }));
	};

	private throttledChange = (value: string) => {
		setTimeout(() => {
			if (value !== this.state.value) {
				return;
			}
			const error = TextBoxValidate(value, this.props.schema);
			this.props.dispatcher.onBlur(
				this.props.schema.id,
				this.state.value,
				error && error.isError,
				error && error.isError && { message: error.message }
			);
		}, 300);
	};

	openNotification = () => {
		this.setState(({ openNotification }) => ({ openNotification: !openNotification }));
	};

	public render() {
		const { schema, errors, visited, parentType, helpButtonClick, openNotificationClick } = this.props;
		// eslint-disable-next-line prefer-const
		let { maxLength, minLength } = schema;

		if (isUndefined(minLength)) {
			minLength = 0;
		}

		const { value } = this.state;
		const errorText = visited?.[schema?.id] && errors && errors?.[schema?.id]?.message

		let status = "default";
		let input = null;
		if ((value || schema?.value) && !errorText) {
			status = "success";
		} else if (errorText) {
			status = "error";
		}

		if (parentType.toUpperCase() === "TEXTAREA") {
			input = (
				<>
					<div style={{ display: "flex" }}>
						<textarea
							style={{ resize: "vertical" }}
							className="form-control"
							onChange={this.onChange}
							onBlur={this.onBlur}
							value={value !== null ? value : this.props.schema?.defaultValue?.ru ?? ""}
							placeholder={this.props.schema?.extra?.placeholder?.ru ?? this.props.schema?.placeholder ?? ""}
							ref={(node) => {
								this.input = node;
							}}
							rows={this.props.schema.lines}
							disabled={this.props.schema.disabled}
						/>
						{helpButtonClick && <Help onClick={helpButtonClick} />}
						{openNotificationClick && <Notification onClick={openNotificationClick} />}
					</div>
					{maxLength && (maxLength > 0 || minLength > 0) && (
						<div className="input-counter">
							{createCounterMessage(
								value !== null ? value : this.props.schema?.defaultValue?.ru ?? "",
								maxLength,
								minLength
							)}
						</div>
					)}
				</>
			);
		} else if (parentType.toUpperCase() === "NUMBER") {
			input = (
				<>
					<div style={{ display: "flex" }}>
						<input
							type="text"
							className="form-control"
							inputMode="decimal"
							onChange={this.onNumberChange}
							onInput={this.onNumberInput}
							onBlur={this.onBlur}
							value={value || ""}
							placeholder={this.props.schema?.extra?.placeholder?.ru ?? this.props.schema?.placeholder ?? ""}
							ref={(node) => {
								this.input = node;
							}}
							disabled={this.props.schema.disabled}
						/>
						{openNotificationClick && <Notification onClick={openNotificationClick} />}
						{helpButtonClick && <Help onClick={helpButtonClick} />}
					</div>
					{maxLength && (maxLength > 0 || minLength > 0) && (
						<div className="input-counter">{createCounterMessage(value, maxLength, minLength)}</div>
					)}
				</>
			);
		} else {
			input = (
				<>
					<div style={{ display: "flex" }}>
						<input
							type="text"
							className="form-control"
							placeholder={this.props.schema?.extra?.placeholder?.ru ?? this.props.schema?.placeholder ?? ""}
							onChange={this.onChange}
							// maxLength={this.props.schema.maxLength + 1}
							onBlur={this.onBlur}
							value={value || ""}
							title={value}
							disabled={this.props.schema.disabled}
							ref={(node) => {
								this.input = node;
							}}
						/>
						{openNotificationClick && <Notification onClick={openNotificationClick} />}
						{helpButtonClick && <Help onClick={helpButtonClick} />}
					</div>
					{maxLength && (maxLength > 0 || minLength > 0) && (
						<div className="input-counter">{createCounterMessage(value, maxLength, minLength)}</div>
					)}
				</>
			);
		}

		return (
			<div
					className={cn(`${status}`, {
					"has-error": !this.props.schema?.disabled && status === "error",
					"has-success": !this.props.schema?.disabled && status === "success",
				})}
		    style={{ width: "100%", marginLeft: 15 }}
			>
				{input}
				{/*{errorText && <Alert>{errorText}</Alert>}*/}
			</div>
		);
	}
}

export function createI18n(
	schema: IControlSchema,
	api: any,
	dispatcher: IDispatcher = null,
	errors: Dictionary<IControlError | undefined>,
	visited: boolean,
	parentType: string,
	helpButtonClick: any,
	openNotificationClick: any
): React.ReactElement<any> {
	return (
		<I18n
			schema={schema as I18nSchema}
			api={api}
			dispatcher={dispatcher}
			errors={errors}
			visited={visited}
			parentType={parentType}
			helpButtonClick={helpButtonClick}
			openNotificationClick={openNotificationClick}
		/>
	);
}
